// https://citrineinformatics.github.io/taurus-documentation/specification/object-templates/
import type { ExplicitType } from "../../../utils";
import { isOfType } from "../../../utils";
import type {
  IConditionTemplatePair,
  IParameterTemplatePair,
  IPropertyTemplatePair,
} from "../attributes";
import type { Uids } from "../utils";

// these values are in common with all object templates
interface IObjectTemplateBase {
  uids: Uids;
  tags: string[];
  name: string;
  description?: string;
  allowed_names?: string[];
  allowed_labels?: string[];
  audit_info?: {
    created_by: string;
    created_at: number;
    updated_by?: string;
    updated_at?: number;
  };
  // data object can sometimes have dataset field, this is an id of which dataset this data object was created in
  dataset?: string;
  persisted?: boolean;
  processing?: boolean;

  conditions?: IConditionTemplatePair[];
  parameters?: IParameterTemplatePair[];
  properties?: IPropertyTemplatePair[];
}

// https://citrineinformatics.github.io/taurus-documentation/specification/object-templates/#material-template
export interface IMaterialTemplate extends IObjectTemplateBase {
  type: "material_template";
  conditions?: never;
  parameters?: never;
}

// https://citrineinformatics.github.io/taurus-documentation/specification/object-templates/#measurement-template
export interface IMeasurementTemplate extends IObjectTemplateBase {
  type: "measurement_template";
}

// https://citrineinformatics.github.io/taurus-documentation/specification/object-templates/#process-template
export interface IProcessTemplate extends IObjectTemplateBase {
  type: "process_template";
  properties?: never;

  allowed_names: string[];
  allowed_labels: string[];
}

export type IObjectTemplate =
  | IMaterialTemplate
  | IMeasurementTemplate
  | IProcessTemplate;
export type ObjectTemplateType = IObjectTemplate["type"];

export const isMaterialTemplate =
  isOfType<IMaterialTemplate>("material_template");

export const isMeasurementTemplate = isOfType<IMeasurementTemplate>(
  "measurement_template"
);

export const isProcessTemplate = isOfType<IProcessTemplate>("process_template");

export const isObjectTemplate = (
  obj?: ExplicitType<string>
): obj is IObjectTemplate =>
  isMaterialTemplate(obj) ||
  isMeasurementTemplate(obj) ||
  isProcessTemplate(obj);
